import { ItemAddonBehaviour } from '@order-mage/integration-sdk';
import { MAX_ADDON_NESTING_DEPTH } from '@/api/menu-list/max-addon-nesting-depth';

export type StepMember = {
    hash: string;
    /** Selected member still shows a variant switch, its own sections, or the chosen variant's sections. */
    hasNested: boolean;
    /** Pieces that come with the item. They are pre-selected and count toward min and max like any other piece. */
    contained?: number;
};

export type StepSection = {
    id: string;
    /** Localized section title, used when telling the customer what is still open. */
    label?: string;
    behaviour?: string | null;
    minAmount?: number | null;
    maxAmount?: number | null;
    autoJumpToNextSection?: boolean | null;
    members: StepMember[];
};

export type UnmetAddonBound = {
    id: string;
    label: string;
    kind: 'min' | 'max';
    bound: number;
};

export type StepSelections = Record<string, string | Record<string, number | boolean> | undefined>;

export type VariantChoice = {
    id: string;
    itemId: string;
    isDefault?: boolean | null;
};

/** Selected amount of one member. A non-numeric quantity counts as zero. */
export const memberAmount = (section: StepSection, selections: StepSelections, hash: string): number => {
    const value = selections[section.id];
    if (section.behaviour === ItemAddonBehaviour.SINGLE_PICK) {
        return value === hash ? 1 : 0;
    }
    if (!value || typeof value === 'string') return 0;
    const raw = value[hash];
    const qty = typeof raw === 'boolean' ? (raw ? 1 : 0) : Number(raw ?? 0);
    return Math.max(0, Number.isFinite(qty) ? qty : 0);
};

/**
 * Every piece in the section, contained ones included, so the bounds read the same number as the
 * section's count badge. A section that already holds its contained pieces is therefore met.
 */
export const getSectionSelectedTotal = (section: StepSection, selections: StepSelections): number =>
    section.members.reduce((sum, member) => sum + memberAmount(section, selections, member.hash), 0);

/**
 * Sections on screen that are under their minimum or over their maximum, including nested ones.
 * A level past the drawing cap is not on screen, so it is not listed.
 */
export const unmetAddonBounds = (
    sections: StepSection[],
    selections: StepSelections,
    depth: number,
    nestedOf: (section: StepSection, depth: number) => StepSection[],
): UnmetAddonBound[] => {
    if (depth >= MAX_ADDON_NESTING_DEPTH) return [];
    return sections.flatMap((section) => {
        const total = getSectionSelectedTotal(section, selections);
        const min = section.minAmount ?? 0;
        const own: UnmetAddonBound[] = [];
        if (total < min) {
            own.push({ id: section.id, label: section.label || section.id, kind: 'min', bound: min });
        } else if (section.maxAmount != null && total > section.maxAmount) {
            own.push({
                id: section.id,
                label: section.label || section.id,
                kind: 'max',
                bound: section.maxAmount,
            });
        }
        return [...own, ...unmetAddonBounds(nestedOf(section, depth), selections, depth + 1, nestedOf)];
    });
};

/**
 * Every section on screen, including nested ones, is inside its min and max.
 * A level past the drawing cap is not on screen, so it does not block.
 */
export const addonTreeMet = (
    sections: StepSection[],
    selections: StepSelections,
    depth: number,
    nestedOf: (section: StepSection, depth: number) => StepSection[],
): boolean => unmetAddonBounds(sections, selections, depth, nestedOf).length === 0;

/** Non-default variant rows a member can switch to; the switch adds its own default choice. */
export const getMemberVariantChoices = <T extends { isDefault?: boolean | null }>(rows: T[]): T[] =>
    rows.filter((row) => !row.isDefault);

// A light green wash, never a solid fill: the tint stays between these percentages of the success color.
const BOOST_MIN_PERCENT = 8;
const BOOST_MAX_PERCENT = 24;

/** Row tint in percent for pieces over the contained ones, strongest once they use up the room left to the maximum. */
export const getMemberBoostPercent = (extra: number, room?: number | null): number => {
    if (!Number.isFinite(extra) || extra <= 0) return 0;
    if (room == null || !Number.isFinite(room))
        return Math.min(BOOST_MAX_PERCENT, BOOST_MIN_PERCENT + 4 * extra);
    if (room <= 0) return BOOST_MAX_PERCENT;
    const share = Math.min(extra, room) / room;
    return Math.round(BOOST_MIN_PERCENT + (BOOST_MAX_PERCENT - BOOST_MIN_PERCENT) * share);
};

/** A selected member still shows a variant switch or a nested card. */
export const isMemberOpeningMore = (variantChoiceCount: number, nestedSectionCount: number): boolean =>
    variantChoiceCount > 0 || nestedSectionCount > 0;

/**
 * Addons on screen for a member: the chosen non-default variant when that variant
 * defines addons, otherwise the member itself.
 */
export const nestedAddonSource = <T extends { addons?: unknown[] | null }>(
    body: T,
    rows: VariantChoice[],
    chosenId: string | undefined,
    bodyForItemId: (itemId: string) => T | undefined,
): T => {
    const active = rows.find((row) => row.id === chosenId) ?? rows.find((row) => row.isDefault);
    if (!active || active.isDefault) return body;
    const variantBody = bodyForItemId(active.itemId);
    return variantBody?.addons?.length ? variantBody : body;
};

const selectedMemberHasNested = (section: StepSection, selections: StepSelections): boolean =>
    section.members.some((member) => member.hasNested && memberAmount(section, selections, member.hash) > 0);

/**
 * Auto-advance only for a single pick, after a real selection, and only when the
 * chosen member has no variant switch and no nested card. Several picks in one
 * section never advance: there is no moment that means the customer is finished.
 */
export const sectionReadyToAdvance = (section: StepSection, selections: StepSelections): boolean => {
    if (section.behaviour !== ItemAddonBehaviour.SINGLE_PICK) return false;
    if (!section.autoJumpToNextSection) return false;
    if (selectedMemberHasNested(section, selections)) return false;
    const total = getSectionSelectedTotal(section, selections);
    if (total <= 0) return false;
    const min = section.minAmount ?? 0;
    if (total < min) return false;
    return true;
};

export type SummaryPackagePrice = {
    id: string;
    amount: number;
    currency: string;
};

const finite = (value: number | undefined): number =>
    typeof value === 'number' && Number.isFinite(value) ? value : 0;

const nonNegative = (value: number | undefined): number => Math.max(0, finite(value));

export type SummaryLineChargeInput = {
    quantity: number;
    contained: number;
    removed: boolean;
    /** Item price of the member, without packages. */
    memberUnit: number;
    /** Set when a non-default variant is selected. Item prices only, without packages. */
    variant?: { itemUnit: number; defaultItemUnit: number };
};

/**
 * Surcharge on one summary row. Pieces that already come with the item stay free.
 * A non-default variant adds its item-price difference for every selected piece.
 * Package money is not included here; those rows are separate.
 */
export const summaryAddonLineCharge = (input: SummaryLineChargeInput): number => {
    if (input.removed) return 0;
    const quantity = nonNegative(input.quantity);
    if (quantity <= 0) return 0;
    const extra = Math.max(0, quantity - nonNegative(input.contained));
    const base = extra > 0 ? finite(input.memberUnit) * extra : 0;
    if (!input.variant) return base;
    return base + (finite(input.variant.itemUnit) - finite(input.variant.defaultItemUnit)) * quantity;
};

const accumulatePackages = (
    totals: Map<string, SummaryPackagePrice>,
    packages: SummaryPackagePrice[],
    factor: number,
) => {
    if (factor === 0) return;
    for (const pkg of packages) {
        if (!pkg.id) continue;
        const amount = finite(pkg.amount) * factor;
        const existing = totals.get(pkg.id);
        if (existing) {
            existing.amount += amount;
            continue;
        }
        totals.set(pkg.id, { id: pkg.id, amount, currency: pkg.currency });
    }
};

export type SummaryPackagesInput = {
    quantity: number;
    contained: number;
    removed: boolean;
    memberPackages: SummaryPackagePrice[];
    /**
     * `null` when the row has no non-default variant.
     * An empty list means the variant has no packages, so the member's packages are not shown.
     */
    variantPackages: SummaryPackagePrice[] | null;
    defaultVariantPackages: SummaryPackagePrice[];
};

/**
 * Packages under a summary row.
 * Without a variant, the member's packages stay, including ones that come with the item, times the selected count.
 * A non-default variant replaces them: every selected piece uses the variant's packages, and pieces that
 * were already included subtract the default variant's packages.
 */
export const summaryAddonPackages = (input: SummaryPackagesInput): SummaryPackagePrice[] => {
    if (input.removed) return [];
    const quantity = nonNegative(input.quantity);
    if (quantity <= 0) return [];
    const totals = new Map<string, SummaryPackagePrice>();
    if (!input.variantPackages) {
        accumulatePackages(totals, input.memberPackages, quantity);
        return [...totals.values()];
    }
    const kept = Math.min(quantity, nonNegative(input.contained));
    accumulatePackages(totals, input.variantPackages, quantity);
    accumulatePackages(totals, input.defaultVariantPackages, -kept);
    return [...totals.values()].filter((pkg) => pkg.amount !== 0);
};
